import { useRef, useState } from 'react';
import { api, errorMessage } from '../api/client';

interface PendingConfirm {
  actionLabel: string;
  resolve: (ok: boolean) => void;
}

export interface PasswordConfirmState {
  open: boolean;
  actionLabel: string;
  password: string;
  error: string | null;
  loading: boolean;
  setPassword: (value: string) => void;
  submit: () => Promise<void>;
  cancel: () => void;
}

/**
 * Re-confirms the signed-in admin's own password before a sensitive action (editing or deleting an
 * account) proceeds. `confirm(label)` resolves true once the password checks out against the
 * server, false if the admin cancels. Render `<PasswordConfirmModal {...state} />` once wherever
 * this hook is used.
 */
export function usePasswordConfirm(): { confirm: (actionLabel: string) => Promise<boolean>; state: PasswordConfirmState } {
  const [open, setOpen] = useState(false);
  const [actionLabel, setActionLabel] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const pending = useRef<PendingConfirm | null>(null);

  const confirm = (label: string): Promise<boolean> =>
    new Promise((resolve) => {
      pending.current = { actionLabel: label, resolve };
      setActionLabel(label);
      setPassword('');
      setError(null);
      setOpen(true);
    });

  const cancel = () => {
    setOpen(false);
    pending.current?.resolve(false);
    pending.current = null;
  };

  const submit = async () => {
    if (!password) {
      setError('Enter your password.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await api.post('/auth/verify-password', { password });
      setLoading(false);
      setOpen(false);
      pending.current?.resolve(true);
      pending.current = null;
    } catch (err) {
      setLoading(false);
      setError(errorMessage(err));
    }
  };

  return {
    confirm,
    state: { open, actionLabel, password, error, loading, setPassword, submit, cancel },
  };
}
